<template>
  <div>
    <nav-bar />
    <v-container class="mt-10 pt-5">
      <!-- Page Header ########################################### -->
      <header-page
        titleText="Gestão de Agentes de Doação"
        titleIcon="fal fa-portrait"
      />
      <v-container pa-6>
        <v-row justify="space-between">
          <v-col cols="auto">
            <p class="list-labels">Lista de Agentes</p>
          </v-col>
          <v-skeleton-loader
            ref="skeleton"
            :boilerplate="false"
            type="table-tfoot"
            :tile="false"
            class="ml-auto"
            v-show="loading"
            min-width="300px"
          ></v-skeleton-loader>
          <v-col cols="auto" v-show="!loading">
            <v-container grid-list-md pa-0>
              <v-layout justify-start wrap justify-sm-end>
                <v-flex col-12 col-sm-6>
                 <v-combobox
                    v-model="currentSearchTerm"
                    :loading="loading"
                    :items="items"
                    label="Buscar"
                    outlined
                    clearable
                    dense
                    @change="addSearchTerm($event, true)"
                  ></v-combobox>
                </v-flex>
                <v-flex flex-grow-0 col-12 col-sm-4>
                  <v-select
                    v-model="selectedOrderBy"
                    :items="orderByOptions"
                    label="Ordenar por"
                    item-text="name"
                    item-value="id"
                    outlined
                    hide-details="auto"
                    dense
                    solo
                    flat
                    @change="loadAgents"
                  ></v-select>
                </v-flex>
              </v-layout>
            </v-container>
          </v-col>
        </v-row>
      </v-container>

      <!-- Search Results ########################################### -->
      <search-result
        inset
        :totalResults="totalNumberOfAgents"
        v-model="searchTerms"
        @itemRemoved="searchTermRemoved()"
        :showAddButton="false"
      />

      <v-skeleton-loader
        ref="skeleton"
        :boilerplate="false"
        type="table-tbody"
        :tile="false"
        class="mx-auto"
        v-show="loading"
      ></v-skeleton-loader>

      <!-- Page Body ########################################### -->
      <v-container>
        <div>
          <template v-for="(agentCouncil, index) in agentCouncils">
            <v-divider
              v-if="index > 0"
              :key="'AgentCouncil' + index"
            ></v-divider>
            <v-layout
              :key="agentCouncil.id"
              class="main-list-item"
              align-center
            >
              <!--avatar #########################-->
              <v-col cols="auto" class="flex-grow-0 pr-2 py-0">
                <v-avatar size="65">
                  <img
                    :src="
                      `https://ui-avatars.com/api/?name=${agentCouncil.agentViewModel.personViewModel.fullName}&size=160`
                    "
                  />
                </v-avatar>
              </v-col>
              <!-- dados -->
              <v-col cols="12" md="6">
                <v-flex pl-2 pt-sm-0 pt-12>
                  <span>{{
                    agentCouncil.councilViewModel.entityViewModel.socialReason
                  }}</span>
                  <h4 class="font-weight-regular">
                    {{ agentCouncil.agentViewModel.personViewModel.fullName }}
                    <span class="caption op-5">
                      ({{
                        agentCouncil.agentViewModel.personViewModel.document
                      }})</span
                    >
                  </h4>
                </v-flex>
              </v-col>

              <!-- tools #########################-->
              <div class="tools">
                <v-container fluid pa-0>
                  <v-row no-wrap align="center" no-gutters>
                    <!-- creation date  #########################-->
                    <v-col cols="auto">
                      <v-layout align-end column>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-flex>
                              <v-icon
                                color="var(--text)"
                                class="mr-2"
                                size="16"
                                v-on="on"
                                >fal fa-user-tag</v-icon
                              >
                              <span class="tiny-label">{{
                                GetAgentAge(
                                  agentCouncil.agentViewModel.personViewModel
                                )
                              }}</span>
                            </v-flex>
                          </template>
                          <span
                            >Cadastrado em:
                            {{
                              agentCouncil.agentViewModel.personViewModel
                                .creationDate | FormatDate
                            }}</span
                          >
                        </v-tooltip>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-flex>
                              <div
                                :class="
                                  GetStatus(
                                    agentCouncil.agentViewModel.lastLogin
                                  )
                                "
                                v-on="on"
                              ></div>
                            </v-flex>
                          </template>
                          <span
                            >Último Login:
                            {{
                              agentCouncil.agentViewModel.lastLogin | FormatDate
                            }}</span
                          >
                        </v-tooltip>
                      </v-layout>
                    </v-col>

                    <v-col cols="auto" class="flex-grow-0 no-wrap">
                      <!-- user / admin & finances #########################-->
                      <v-layout>
                        <div class="ml-3">
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-chip
                                class="mr-3"
                                x-small
                                v-on="on"
                                dark
                                color="var(--danger)"
                                >{{
                                  agentCouncil.agentDonationViewModel
                                    .paidDonationsQuantity
                                }}</v-chip
                              >
                            </template>
                            <span>Doações feitas</span>
                          </v-tooltip>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="danger--text">{{
                                agentCouncil.agentDonationViewModel
                                  .paidDonationsValue | FormatMoney
                              }}</span>
                            </template>
                            <span>Valor doado</span>
                          </v-tooltip>
                        </div>
                      </v-layout>
                    </v-col>

                    <!-- edit & on/off #########################-->
                    <v-col cols="auto">
                      <v-layout
                        align-center
                        class="d-none d-md-flex flex-grow-0"
                      >
                        <v-flex pl-6>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                icon
                                fab
                                small
                                color="var(--gray)"
                                class="hover-red"
                                v-on="on"
                                @click="editAgent(agentCouncil)"
                              >
                                <v-icon size="20">fal fa-pen</v-icon>
                              </v-btn>
                            </template>
                            <span>Editar</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex pl-6>
                          <v-switch
                            v-model="agentCouncil.agentViewModel.active"
                            @change="
                              updateAgentStatus(agentCouncil.agentViewModel.id)
                            "
                            inset
                          >
                          </v-switch>
                        </v-flex>
                      </v-layout>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
            </v-layout>
          </template>
        </div>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NavBar from "@/components/NavBar";
import HeaderPage from "@/components/HeaderPage";
import SearchResult from "@/components/SearchResult";
import PersonService from "@/scripts/services/person.service";
import AgentOrderByOptions from "@/scripts/enums/agentOrderByOptions.enum";
import CommonHelper from "@/scripts/helpers/common.helper";
import Routes from "@/router/names.js";

export default {
  name: "AgentsManagement",
  components: {
    NavBar,
    HeaderPage,
    SearchResult
  },
  data: () => ({
    totalNumberOfAgents: 0,
    searchTerms: [],
    agentCouncils: [],
    items: [],
    personService: new PersonService(),
    loadingCount: false,
    loadingList: false,
    currentSearchTerm: "",
    searchWaitTime: 2000,
    selectedOrderBy: AgentOrderByOptions.Name,
    orderByOptions: [
      { id: AgentOrderByOptions.CreationDate, name: "Ordenar por data" },
      { id: AgentOrderByOptions.Name, name: "Ordenar por nome" },
      { id: AgentOrderByOptions.Status, name: "Ordenar por status" }
    ],
    contextMenu: [
      { icon: "fal fa-pen", menu: "Editar" },
      { icon: "fal fa-ban", menu: "Desativar" }
    ]
  }),
  mounted() {
    this.loadAgents();
  },
  computed: {
    ...mapGetters("userModule", ["loggedEntityId"]),
    loading() {
      return this.loadingList || this.loadingCount;
    }
  },
  methods: {
    ...mapActions(["setSelectedAgent"]),
    getFilters() {
      return {
        orderBy: this.selectedOrderBy,
        currentPage: this.currentPage - 1,
        pageSize: this.selectedPagination,
        searchTerms: this.searchTerms,
        loggedEntityId: parseInt(this.loggedEntityId)
      };
    },
    loadAgents() {
      if (this.loading) return;
      this.loadingCount = true;
      this.loadingList = true;
      this.searchTimeout = null;
      this.personService
        .countAgents(this.getFilters())
        .then(this.countAgentsCallBack)
        .finally(() => (this.loadingCount = false));
      this.personService
        .listAgents(this.getFilters())
        .then(this.listAgentsCallBack)
        .finally(() => (this.loadingList = false));
    },
    listAgentsCallBack(data) {
      this.agentCouncils = data;
       if(this.items.length == 0) {
        this.agentCouncils.forEach(element => {
          this.items.push(element.agentViewModel.personViewModel.fullName);
        });
        this.item = this.items.sort();
      }
      this.loadingList = false;
    },
    countAgentsCallBack(data) {
      this.totalNumberOfAgents = data;
      this.loadingCount = false;
    },
    searchTermRemoved() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(this.loadAgents, this.searchWaitTime);
    },
    addSearchTerm(event, addAnyway) {
         if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(this.loadAgents, this.searchWaitTime);
      }
      if (event.key == "Enter" || addAnyway) {
        if (
          this.currentSearchTerm &&
          this.currentSearchTerm.length > 0 &&
          this.searchTerms.indexOf(this.currentSearchTerm) == -1
        )
          this.searchTerms.push(this.currentSearchTerm);
        this.currentSearchTerm = [];
        if (!this.searchTimeout)
          this.searchTimeout = setTimeout(this.loadAgents, this.searchWaitTime);
      }
    },
    GetAgentAge(entity) {
      return CommonHelper.GetEntityAge(entity);
    },
    editAgent(agentCouncil) {
      this.setSelectedAgent(agentCouncil.agentViewModel);
      this.$router.push({
        name: Routes.web.AgentProfile,
        params: { agentId: agentCouncil.agentViewModel.id }
      });
    },
    updateAgentStatus(agentId) {
      this.personService.updateAgentStatus(agentId);
    },
    GetStatus(lastLogin) {
      if (!lastLogin) {
        return "login-status never";
      }

      let lastLoginDateDayOfTheMonth = new Date(lastLogin).getDate();

      var today = new Date();
      var dd = String(today.getDate()).padStart(2, "0");

      var lastLoginNUmberOfDays = dd - lastLoginDateDayOfTheMonth;

      if (lastLoginNUmberOfDays == 0) {
        return "login-status ok";
      } else if (lastLoginNUmberOfDays == 1) {
        return "login-status _24h";
      } else if (lastLoginNUmberOfDays >= 2) {
        return "login-status _48h";
      }
    }
  }
};
</script>